import React, { Component, ErrorInfo, ReactNode } from 'react'
import { Translation } from 'react-i18next'

interface Props {
  children: ReactNode
}

interface State {
  hasError: boolean
  error: Error | null
}

export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props)
    this.state = { hasError: false, error: null }
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error }
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('Error caught by boundary:', error, errorInfo)

    // In production, send to error tracking service
    // Example: Sentry.captureException(error, { extra: errorInfo })
  }

  render() {
    if (this.state.hasError) {
      return (
        <Translation>
          {(t) => (
            <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
              <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-8">
                <div className="flex items-center justify-center w-12 h-12 mx-auto bg-red-100 rounded-full">
                  <svg
                    className="w-6 h-6 text-red-600"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                    />
                  </svg>
                </div>
                <h1 className="mt-4 text-xl font-bold text-center text-gray-900">
                  {t('common.somethingWentWrong')}
                </h1>
                <p className="mt-2 text-sm text-center text-gray-600">
                  {t('common.unexpectedError')}
                </p>
                {process.env.NODE_ENV === 'development' && this.state.error && (
                  <pre className="mt-4 p-4 bg-gray-100 rounded text-xs overflow-auto max-h-48">
                    {this.state.error.toString()}
                  </pre>
                )}
                <div className="mt-6 flex space-x-3">
                  <button
                    onClick={() => window.location.reload()}
                    className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                  >
                    {t('common.reloadPage')}
                  </button>
                  <button
                    onClick={() => (window.location.href = '/')}
                    className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50"
                  >
                    {t('common.goToHome')}
                  </button>
                </div>
              </div>
            </div>
          )}
        </Translation>
      )
    }

    return this.props.children
  }
}
